import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { legalDocumentsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import type { LegalDocumentListItem, OrganizationAgreementStatus } from '@pillar-portal/api-types';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { LegalDocumentAssignmentEditor } from './LegalDocumentAssignmentEditor';
import { SYSTEM_DISPLAY_TIME_ZONE, formatTimestampForMerchantUi } from '@pillar-portal/api-types';
import { LegalDocumentReview } from './LegalDocumentReview';
import { RemoveOrganizationAssignmentButton } from './RemoveOrganizationAssignmentButton';

interface OrganizationAgreementReviewPageProps {
    readonly organizationUuid: string;
    readonly organizationName: string;
    readonly allowAcceptance: boolean;
    /** Internal administrators can assign agreements to this Organization here. */
    readonly allowAssignment?: boolean;
}

function formatAgreementValidity(agreement: OrganizationAgreementStatus): string {
    if (agreement.validity_kind === 'indefinite') {
        return 'Indefinite';
    }
    return `${agreement.acceptance_validity_duration_days} days from each acceptance`;
}

function formatAcceptanceExpiry(agreement: OrganizationAgreementStatus): string {
    if (agreement.latest_valid_acceptance === null) {
        return 'No current valid acceptance';
    }
    if (agreement.validity_kind === 'indefinite') {
        return 'No expiration';
    }
    const expiry = new Date(agreement.latest_valid_acceptance.accepted_ts);
    expiry.setUTCDate(expiry.getUTCDate() + agreement.acceptance_validity_duration_days);
    return formatTimestampForMerchantUi(expiry.toISOString(), SYSTEM_DISPLAY_TIME_ZONE);
}

export function OrganizationAgreementReviewPage({
    organizationUuid,
    organizationName,
    allowAcceptance,
    allowAssignment = false,
}: OrganizationAgreementReviewPageProps) {
    const [assigningDocument, setAssigningDocument] = useState<LegalDocumentListItem | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const queryClient = useQueryClient();
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [reviewingAssignmentUuid, setReviewingAssignmentUuid] = useState<string | null>(null);
    const [signerAuthorityConfirmed, setSignerAuthorityConfirmed] = useState(false);
    const queryKeyPrefix = useMemo(
        () => ['organizationAgreements', organizationUuid] as const,
        [organizationUuid]
    );
    const acceptMutation = useMutation({
        mutationFn: async (legalDocumentAssignmentUuid: string) =>
            await legalDocumentsApi.acceptOrganizationAgreement(organizationUuid, {
                legal_document_assignment_uuid: legalDocumentAssignmentUuid,
                signer_authority_confirmed: true,
            }),
        onSuccess: async () => {
            setMutationError(null);
            setReviewingAssignmentUuid(null);
            setSignerAuthorityConfirmed(false);
            await queryClient.invalidateQueries({ queryKey: queryKeyPrefix });
        },
        onError: (error: unknown) => {
            setMutationError(
                error instanceof Error
                    ? error.message
                    : 'Unable to accept the Organization agreement.'
            );
        },
    });

    return (
        <div className="space-y-3">
            {allowAcceptance ? (
                <p className="text-sm text-gray-600">
                    These agreements must be signed on behalf of {organizationName} before payment
                    services can be activated. Open an agreement, read it to the end, and sign it.
                </p>
            ) : null}
            {allowAssignment ? (
                <div className="space-y-3 rounded border p-4">
                    {isAssigning ? (
                        <>
                            <OmnisearchSelect<LegalDocumentListItem, string>
                                label="Legal document to assign"
                                placeholder="Search legal documents by title..."
                                pageSize={25}
                                selectedItemId={assigningDocument?.legal_document_uuid ?? null}
                                selectedItemLabel={
                                    assigningDocument === null
                                        ? undefined
                                        : `${assigningDocument.legal_document_title} (${assigningDocument.legal_document_version})`
                                }
                                onClearSelection={() => setAssigningDocument(null)}
                                fetchPage={async (params) => {
                                    const response =
                                        await legalDocumentsApi.searchLegalDocumentsPage({
                                            ...(params.terms !== undefined
                                                ? { terms: [...params.terms] }
                                                : {}),
                                            limit: params.limit,
                                            cursor: params.cursor,
                                        });
                                    return {
                                        items: response.legal_documents.filter(
                                            (document) => document.status === 'ACTIVE'
                                        ),
                                        pagination: response.pagination,
                                    };
                                }}
                                getItemId={(item) => item.legal_document_uuid}
                                getItemLabel={(item) =>
                                    `${item.legal_document_title} (${item.legal_document_version})`
                                }
                                getItemFilterText={(item) =>
                                    `${item.legal_document_title} ${item.legal_document_version}`
                                }
                                onSelect={setAssigningDocument}
                            />
                            {assigningDocument === null ? null : (
                                <LegalDocumentAssignmentEditor
                                    key={assigningDocument.legal_document_uuid}
                                    legalDocument={assigningDocument}
                                    organization={{
                                        organization_uuid: organizationUuid,
                                        name: organizationName,
                                    }}
                                    onAssigned={() => {
                                        setAssigningDocument(null);
                                        setIsAssigning(false);
                                        runInBackground(
                                            queryClient.invalidateQueries({
                                                queryKey: queryKeyPrefix,
                                            }),
                                            () => undefined
                                        );
                                    }}
                                />
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setAssigningDocument(null);
                                    setIsAssigning(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button type="button" onClick={() => setIsAssigning(true)}>
                            Assign agreement to {organizationName}
                        </Button>
                    )}
                </div>
            ) : null}
            {mutationError === null ? null : (
                <div
                    role="alert"
                    className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                    {mutationError}
                </div>
            )}
            <InfiniteScrollList<OrganizationAgreementStatus, string, typeof queryKeyPrefix>
                pageTitle="Organization agreements"
                cardTitle={`Organization agreements assigned to ${organizationName}`}
                queryPlaceholder="Search agreements"
                queryKeyPrefix={queryKeyPrefix}
                pageSize={25}
                showPageHeader={false}
                showQueryInput={false}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchOrganizationAgreementsPage(
                        organizationUuid,
                        {
                            limit: params.limit,
                            cursor: params.cursor,
                        }
                    );
                    return {
                        items: response.organization_agreements,
                        pagination: response.pagination,
                    };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    return z.string().parse(pageParam);
                }}
                stateCopy={{
                    loadingLabel: 'Loading Organization agreements...',
                    emptyLabel: 'No Organization agreement is assigned to this exact Organization.',
                    endOfListLabel: 'End of Organization agreements.',
                    genericErrorLabel: 'Unable to load Organization agreements.',
                }}
                renderItems={({ items }) => (
                    <div className="space-y-3">
                        {items.map((agreement) => (
                            <div
                                key={agreement.legal_document_assignment_uuid}
                                className="rounded border p-4 text-sm"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="font-medium">
                                        {agreement.legal_document.legal_document_title} (
                                        {agreement.legal_document.legal_document_version})
                                    </div>
                                    {allowAssignment ? (
                                        <RemoveOrganizationAssignmentButton
                                            organizationUuid={organizationUuid}
                                            legalDocumentAssignmentUuid={
                                                agreement.legal_document_assignment_uuid
                                            }
                                            documentLabel={`${agreement.legal_document.legal_document_title} (${agreement.legal_document.legal_document_version})`}
                                        />
                                    ) : null}
                                </div>
                                <div>
                                    Organization: {agreement.organization.organization_name} (
                                    {agreement.organization.organization_uuid})
                                </div>
                                <div>Scope: Exact Organization only; no inheritance</div>
                                <div>Document: {agreement.legal_document.legal_document_uuid}</div>
                                <div>Assignment: {agreement.legal_document_assignment_uuid}</div>
                                <div>Digest: {agreement.legal_document.content_sha256}</div>
                                <div>Validity: {formatAgreementValidity(agreement)}</div>
                                <div>Expiry: {formatAcceptanceExpiry(agreement)}</div>
                                {agreement.latest_valid_acceptance === null ? (
                                    <div>Signer: No current valid acceptance</div>
                                ) : (
                                    <>
                                        <div>
                                            Accepted:{' '}
                                            {formatTimestampForMerchantUi(
                                                agreement.latest_valid_acceptance.accepted_ts,
                                                SYSTEM_DISPLAY_TIME_ZONE
                                            )}
                                        </div>
                                        <div>
                                            Signer User:{' '}
                                            {
                                                agreement.latest_valid_acceptance
                                                    .accepted_by_user_uuid
                                            }
                                        </div>
                                        <div>
                                            Signer RoleAssignment:{' '}
                                            {
                                                agreement.latest_valid_acceptance
                                                    .accepted_by_role_assignment_uuid
                                            }
                                        </div>
                                    </>
                                )}
                                {agreement.latest_valid_acceptance !== null ? (
                                    <div>
                                        Signer authority confirmed:{' '}
                                        {agreement.latest_valid_acceptance
                                            .signer_authority_confirmed
                                            ? 'Yes'
                                            : 'Not recorded'}
                                    </div>
                                ) : null}
                                {allowAcceptance &&
                                agreement.latest_valid_acceptance === null &&
                                reviewingAssignmentUuid !==
                                    agreement.legal_document_assignment_uuid ? (
                                    <Button
                                        type="button"
                                        className="mt-3"
                                        onClick={() => {
                                            setMutationError(null);
                                            setSignerAuthorityConfirmed(false);
                                            setReviewingAssignmentUuid(
                                                agreement.legal_document_assignment_uuid
                                            );
                                        }}
                                    >
                                        Review and sign
                                    </Button>
                                ) : null}
                                {allowAcceptance &&
                                agreement.latest_valid_acceptance === null &&
                                reviewingAssignmentUuid ===
                                    agreement.legal_document_assignment_uuid ? (
                                    <div className="mt-3">
                                        <LegalDocumentReview
                                            legalDocumentUuid={
                                                agreement.legal_document.legal_document_uuid
                                            }
                                            title={agreement.legal_document.legal_document_title}
                                            acceptLabel={`Sign for ${organizationName}`}
                                            acceptPending={acceptMutation.isPending}
                                            confirmation={{
                                                label: `I am authorized to sign this agreement on behalf of ${organizationName} and to bind it legally.`,
                                                checked: signerAuthorityConfirmed,
                                                onChange: setSignerAuthorityConfirmed,
                                            }}
                                            onAccept={() =>
                                                acceptMutation.mutate(
                                                    agreement.legal_document_assignment_uuid
                                                )
                                            }
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            />
        </div>
    );
}
